import React, { CSSProperties, FC, useRef } from 'react';
import { AddressTransactionsFilterTxType } from '@apolloGenerated';
import { useIsEllipsis } from '@hooks/useIsEllipsis';
import { CopyHash, Tooltip, Typography, TypographyVariantType } from '@rubin-dev/goblin';
import { format } from '@aml/amount';

export interface TableCellAmountProps {
  amount: string | number;
  type?: AddressTransactionsFilterTxType;
  style?: CSSProperties;
  variant?: TypographyVariantType;
  canCopy?: boolean;
}

export const TableCellAmount: FC<TableCellAmountProps> = ({
  amount,
  type = AddressTransactionsFilterTxType.All,
  style,
  variant = 'body-14',
  canCopy = false,
}): JSX.Element => {
  const textRef = useRef<HTMLElement>(null);
  const isEllipsis = useIsEllipsis(textRef, amount);
  const isSentTr = type === AddressTransactionsFilterTxType.Sent;
  const isReceiveTr = type === AddressTransactionsFilterTxType.Receives;
  const color = isSentTr ? 'error-2' : isReceiveTr ? 'success-2' : 'on-surface-primary-1';
  const mathOperator = isSentTr ? '-' : isReceiveTr ? '+' : '';

  const amountValue = `${mathOperator}${format(
    parseFloat(String(amount)).toFixed(2),
    2,
  )}`;

  return (
    <Tooltip
      placement="top"
      disabled={!amount || !isEllipsis}
      label={
        <Typography variant={variant} color={color}>
          {amountValue}
        </Typography>
      }
    >
      {canCopy ? (
        <CopyHash
          label={
            amountValue.length >= 22 ? amountValue.slice(0, 22) + '...' : amountValue
          }
          hash={String(amount)}
          variant={variant}
          defaultColor={color}
          withoutButton
          style={{ cursor: 'pointer' }}
        />
      ) : (
        <>
          <Typography
            variant={variant}
            color={color}
            style={{ cursor: isEllipsis ? 'pointer' : 'text', ...style }}
            ref={textRef}
          >
            {amountValue.length >= 10 ? amountValue.slice(0, 10) + '...' : amountValue}
          </Typography>
        </>
      )}
    </Tooltip>
  );
};

export const renderCellAmount = (args: TableCellAmountProps) => (
  <TableCellAmount {...args} />
);
